import React from "react";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import dayjs from "dayjs";
import { pickerFieldSX } from "../utils/formField";
import themestyle from "../theme";

// Dropdown styling: green selected cells (matching the field's green focus border) and no
// vertical divider line between the hour/minute columns.
const clockPopupSX = {
  "& .MuiMultiSectionDigitalClockSection-root": {
    borderRight: "none",
    borderLeft: "none",
  },
  "& .MuiMultiSectionDigitalClockSection-item.Mui-selected": {
    backgroundColor: themestyle.colors.activeGreen,
    "&:hover, &:focus": { backgroundColor: themestyle.colors.activeGreen },
  },
};

/**
 * App-wide time picker. Always 24-hour (no AM/PM "aa"), consistent "HH:mm" format and the
 * MUI digital-clock dropdown. Works with plain "HH:mm" strings so it drops into existing
 * string-based form state — value in, value out.
 */
export type AppTimePickerProps = {
  value: string; // "HH:mm" or ""
  onChange: (value: string) => void;
  // Fires with MUI's validation reason ("invalidDate", "minTime", …) whenever the
  // entered value can't be accepted — including an incomplete entry like "13:--" —
  // or null when the value becomes clean. Lets the form show an explicit
  // "Invalid time" message instead of silently leaving the field blank.
  onValidationError?: (reason: string | null) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  /** Earliest selectable time, as "HH:mm". */
  minTime?: string;
  fullWidth?: boolean;
};

const AppTimePicker: React.FC<AppTimePickerProps> = ({
  value,
  onChange,
  onValidationError,
  disabled,
  error,
  helperText,
  minTime,
  fullWidth = true,
}) => (
  // Desktop variant = inline dropdown (no mobile CANCEL/OK modal), consistent on every viewport.
  <DesktopTimePicker
    ampm={false}
    format="HH:mm"
    // Two columns (hours + minutes), every minute — mirrors the native picker dropdown.
    views={["hours", "minutes"]}
    minutesStep={1}
    value={value ? dayjs(value, "HH:mm") : null}
    onChange={(v) => onChange(v && v.isValid() ? v.format("HH:mm") : "")}
    // onError catches a *complete* but rejected value (e.g. before minTime -> "minTime").
    // An INCOMPLETE entry ("13:--") is reported by MUI as empty, so onError/onChange
    // stay silent for it — the form detects that case on blur of the wrapper instead.
    onError={(reason) => onValidationError?.((reason as string | null) ?? null)}
    disabled={disabled}
    minTime={minTime ? dayjs(minTime, "HH:mm") : undefined}
    slotProps={{
      textField: {
        fullWidth,
        sx: pickerFieldSX,
        error,
        helperText,
      },
      desktopPaper: { sx: clockPopupSX },
      // No CANCEL/OK action bar — the inline dropdown commits on selection.
      actionBar: { actions: [] },
    }}
  />
);

export default AppTimePicker;
